import { Dirary } from '../../types/dirary'

//開始時刻と終了時刻から合計時間を計算する
//12:00-13:00の間の時間は計算しない。
//つまり、8:30-13:30の場合は、4時間となる。8:30-12:30の場合は3時間30分となる。
//帰り値は{hour:number,min:number,text:string}とする。
//例：{hour:3,min:30,text:"03時間30分"}
export const calcTotalTimeByFromAndTo = (
  fromTime: string,
  toTime: string,
  hasBreak: boolean
): { hour: number; min: number; text: string } => {
  // 時刻をDateオブジェクトに変換するためのベース日付
  const baseDate = '2024-01-01'

  // 時刻をDateオブジェクトに変換する関数
  const parseTime = (time: string): Date => new Date(`${baseDate}T${time}:00`)

  // 入力された開始時刻と終了時刻
  const startTime = parseTime(fromTime)
  const endTime = parseTime(toTime)

  // 昼休みの開始時刻と終了時刻
  const breakStart = parseTime('12:00')
  const breakEnd = parseTime('13:00')

  // 合計時間を分で計算
  let totalMinutes: number

  if (endTime <= breakStart) {
    // 終了時刻が昼休み開始前の場合
    totalMinutes = (endTime.getTime() - startTime.getTime()) / 60000
  } else if (startTime >= breakEnd) {
    // 開始時刻が昼休み終了後の場合
    totalMinutes = (endTime.getTime() - startTime.getTime()) / 60000
  } else if (startTime < breakStart && endTime > breakEnd) {
    // 開始時刻が昼休み前、終了時刻が昼休み後の場合
    totalMinutes =
      (breakStart.getTime() -
        startTime.getTime() +
        (endTime.getTime() - breakEnd.getTime())) /
      60000
  } else if (startTime < breakStart && endTime <= breakEnd) {
    // 開始時刻が昼休み前、終了時刻が昼休み中の場合
    totalMinutes = (breakStart.getTime() - startTime.getTime()) / 60000
  } else if (startTime >= breakStart && endTime > breakEnd) {
    // 開始時刻が昼休み中、終了時刻が昼休み後の場合
    totalMinutes = (endTime.getTime() - breakEnd.getTime()) / 60000
  } else {
    // その他のケース（通常は考えにくいが念のため）
    totalMinutes = 0
  }

  // hasBreakがtrueの場合、30分引く
  if (hasBreak) {
    totalMinutes = Math.max(0, totalMinutes - 30)
  }

  // 時間と分を計算
  const hours = totalMinutes / 60
  const minutes = totalMinutes

  // 結果を返す
  const formattedHours = Math.floor(hours).toString().padStart(2, '0')
  const formattedMinutes = (minutes % 60).toString().padStart(2, '0')
  const resultText = `${formattedHours}時間${formattedMinutes}分`

  return { hour: hours, min: minutes, text: resultText }
}

// export type Dirary = {
//   id: number; //日報ID
//   workerId: string; //社員番号
//   workerName: string; //社員名
//   departmentId: number; //部署ID
//   affiliationFactoryId: number; //所属工場ID
//   jobId: number; //役職コード
//   workingDate: string; //作業開始日
//   factoryId: number; //製作工場
//   constructionId: number; //工事区分
//   startTime: string; //作業開始時間
//   endTime: string; //作業終了時間
//   isBreak: boolean; //30分休憩
//   totalWorkingTimeByHour: number; //合計作業時間(時間)
//   totalWorkingTimeByMinute: number; //合計作業時間(分)
//   productNo: string; //製作番号1
//   productBno: string; //製作番号2
//   siteName: string; //現場名
//   slipNumber: string; //伝票番号
//   processName: string; //工程名
//   processId: string; //工程ID
//   estimatedTime: number; //予定工数
//   workingTimeByHour: number; //作業時間(時間)
//   workingTimeByMinute: number; //作業時間(分)
//   comment: string; //コメント
// };

/**
 *fromTimeは"08:00"のような文字列であり、diaryはDirary[]型である。この中で、workingTimeByHourとworkingTimeByMinuteを合計して
 、合計時間を計算してほしい。なお、合計時間は{hour:number,min:number,text:string}として返すこととする。またfromTimeは昼休みの判定用の変数である。
 　　fromTimeからdiaryの作業時間が経過した時時間が、12:00から13:00にかぶる場合は、その時間はカウントしないものとする。
 　　hourとminにはそれぞれ、合計時間を時間単位と分単位で返し、textには"03時間30分"のような形式で返すこととする。
 
 例えば、fromTime08:30、workingTimeByHour 1、wokingTimeByMin 60、workingTimeByHour 0.5、wokingTimeByMin 30の場合は結果のhourは1.5となる。
また、fromTime11:00、workingTimeByHour 1、wokingTimeByMin 60、workingTimeByHour 0.5、wokingTimeByMin 30の場合は結果のhourは1.0となる。
 ES6で記述してほしい。関数名は以下の通りとしてほしい。
 export const calcTotalTimeByDiary = (fromTime: string, diary: Dirary[]) 
 */

export const calcTotalTimeByDiary = (
  fromTime: string,
  diary: Dirary[],
  hasBreak: boolean,
  breakTime: number
) => {
  const lunchBreakStart = 12 * 60 // 12:00 in minutes
  const lunchBreakEnd = 13 * 60 // 13:00 in minutes

  let totalMinutes = 0

  const fromTimeInMinutes = (() => {
    const [hours, minutes] = fromTime.split(':').map(Number)
    return hours * 60 + minutes
  })()

  diary.forEach((entry) => {
    totalMinutes += entry.workingTimeByHour * 60
  })

  let startMinutes = fromTimeInMinutes // 開始時刻（分）
  let endMinutes = fromTimeInMinutes + totalMinutes // 終了時刻（分）

  // 昼休みの時間を考慮
  if (startMinutes < lunchBreakStart && endMinutes > lunchBreakStart) {
    endMinutes += 60 // 昼休み分を加算
  }
  if (hasBreak) endMinutes += Number(breakTime) // 休憩時間を足す

  // console.log(
  //   `作業時間${totalMinutes}、 開始時刻${startMinutes}、終了時刻${endMinutes} 昼休憩開始${lunchBreakStart}、昼休憩終了${lunchBreakEnd}`
  // );

  const finalTotalMinutes = totalMinutes
  const finalTotalHours = finalTotalMinutes / 60
  const formattedHours = Math.floor(finalTotalHours).toString().padStart(2, '0')
  const formattedMinutes = (finalTotalMinutes % 60).toString().padStart(2, '0')
  const finalTotalText = `${formattedHours}時間${formattedMinutes}分`

  const endHours = Math.floor(endMinutes / 60)
    .toString()
    .padStart(2, '0')
  const endMinutesFormatted = (endMinutes % 60).toString().padStart(2, '0')
  const endTime = `${endHours}:${endMinutesFormatted}`

  return {
    hour: finalTotalHours,
    min: finalTotalMinutes,
    text: finalTotalText,
    endTime: endTime,
  }
}
